// src/context/AuthContext.js
"use client";
/**
 * ======================================================
 * ✅ Contexto de Autenticación – Evalúa Salud IPS
 * ------------------------------------------------------
 * - Persistencia con /auth/me
 * - Login con /auth/login
 * - Renovación segura de JWT
 * - Sincronización entre pestañas
 * - Control global de login/logout
 * ======================================================
 */

import PropTypes from "prop-types";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import {
  iniciarSesion as loginAPI,
  obtenerPerfil,
  renovarToken
} from "../services/authService";
import api, { isPublicQrPath } from "../api/axiosConfig";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => {
    try {
      return localStorage.getItem("token");
    } catch {
      return null;
    }
  });

  const [usuario, setUsuario] = useState(null);
  const [cargando, setCargando] = useState(true);

  const abortRef = useRef(null);
  const mountedRef = useRef(false);

  // ======================================================
  // 🔐 Header Authorization global
  // ======================================================
  const setAuthHeader = useCallback((jwt) => {
    const headers = api?.defaults?.headers?.common;
    if (!headers) {return;}

    if (jwt) {
      headers.Authorization = `Bearer ${jwt}`;
    } else {
      delete headers.Authorization;
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    setAuthHeader(token);
    return () => {
      mountedRef.current = false;
      if (abortRef.current?.abort) {abortRef.current.abort();}
    };
  }, [token, setAuthHeader]);

  // ======================================================
  // 🚪 Cerrar sesión
  // ======================================================
  const cerrarSesion = useCallback(() => {
    try {
      localStorage.removeItem("token");
    } catch {
      /* noop */
    }
    setAuthHeader(null);
    setToken(null);
    setUsuario(null);
  }, [setAuthHeader]);

  // ======================================================
  // 👤 Verificar sesión actual (/auth/me)
  // ======================================================
  const verificarSesion = useCallback(async () => {
    if (!token) {
      setUsuario(null);
      return false;
    }

    try {
      if (abortRef.current?.abort) {abortRef.current.abort();}
      abortRef.current = new AbortController();

      const data = await obtenerPerfil();
      const perfil = data?.usuario ?? data;

      if (!perfil || !perfil._id) {
        cerrarSesion();
        return false;
      }

      setUsuario(perfil);
      return true;
    } catch {
      cerrarSesion();
      return false;
    }
  }, [token, cerrarSesion]);

  // ======================================================
  // 🔐 Iniciar sesión (/auth/login)
  // ======================================================
  const iniciarSesion = useCallback(
    async (email, password) => {
      setCargando(true);
      try {
        const data = await loginAPI(email, password);

        const nuevoToken = data?.token;
        const perfil = data?.usuario ?? data;

        if (!nuevoToken) {
          throw new Error("El servidor no devolvió un token válido.");
        }

        localStorage.setItem("token", nuevoToken);
        setToken(nuevoToken);
        setAuthHeader(nuevoToken);

        if (perfil && perfil._id) {
          setUsuario(perfil);
        } else {
          await verificarSesion();
        }

        return perfil || null;
      } catch (error) {
        const msg =
          error?.response?.data?.mensaje ||
          error?.response?.data?.error ||
          error?.message ||
          "Error al iniciar sesión.";
        throw new Error(msg);
      } finally {
        if (mountedRef.current) {setCargando(false);}
      }
    },
    [setAuthHeader, verificarSesion]
  );

  // ======================================================
  // ♻ Renovar token JWT (/auth/renew)
  // ======================================================
  const renovarSesion = useCallback(async () => {
    try {
      const data = await renovarToken();
      const nuevoToken = data?.token;
      const perfil = data?.usuario;

      if (nuevoToken) {
        localStorage.setItem("token", nuevoToken);
        setToken(nuevoToken);
        setAuthHeader(nuevoToken);
      }

      if (perfil) {setUsuario(perfil);}
    } catch {
      cerrarSesion();
    }
  }, [cerrarSesion, setAuthHeader]);

  // ======================================================
  // 🧠 Verificar al montar
  // ======================================================
  useEffect(() => {
    (async () => {
      if (isPublicQrPath(window.location?.pathname || "")) {
        setCargando(false);
        return;
      }

      setCargando(true);
      await verificarSesion();
      setCargando(false);
    })();
  }, [verificarSesion]);

  // ======================================================
  // 🔄 Sincronización entre pestañas
  // ======================================================
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== "token") {return;}

      const nuevo = e.newValue;
      if (nuevo) {
        setToken(nuevo);
        setAuthHeader(nuevo);
        void verificarSesion();
      } else {
        cerrarSesion();
      }
    };

    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [cerrarSesion, verificarSesion, setAuthHeader]);

  // ======================================================
  // 📦 Valor expuesto al contexto
  // ======================================================
  const valor = useMemo(
    () => ({
      usuario,
      token,
      cargando,
      autenticado: Boolean(token && usuario),
      iniciarSesion,
      verificarSesion,
      cerrarSesion,
      renovarSesion
    }),
    [usuario, token, cargando, iniciarSesion, verificarSesion, cerrarSesion, renovarSesion]
  );

  return (
    <AuthContext.Provider value={valor}>
      {!cargando && children}
    </AuthContext.Provider>
  );
}

AuthProvider.propTypes = { children: PropTypes.node.isRequired };

// ======================================================
// 🪪 Hook personalizado
// ======================================================
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {throw new Error("useAuth debe usarse dentro de un <AuthProvider>");}
  return ctx;
}
